/**
 * Suggested questions: a tag-style input with the API limits (count and 80 characters each).
 * The component library has no tags input, so this wraps Mantine's TagsInput.
 */

import { useState } from 'react';
import { TagsInput } from '@mantine/core';
import { HelpLimits } from '@actschurch/ccms-sdk';

interface QuestionsInputProps {
  label: string;
  description?: string;
  value: string[];
  onChange: (value: string[]) => void;
  max: number;
  disabled?: boolean;
}

export function QuestionsInput({ label, description, value, onChange, max, disabled }: QuestionsInputProps) {
  const [error, setError] = useState<string | null>(null);
  const maxLen = HelpLimits.maxSuggestedQuestionLength;

  const handle = (next: string[]) => {
    const tooLong = next.find((q) => q.trim().length > maxLen);
    if (tooLong) {
      setError(`Keep each question to ${maxLen} characters or fewer.`);
      return;
    }
    const seen = new Set<string>();
    const clean = next
      .map((q) => q.trim())
      .filter((q) => {
        const k = q.toLowerCase();
        if (!q || seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .slice(0, max);
    setError(null);
    onChange(clean);
  };

  return (
    <TagsInput
      label={label}
      description={description ?? `Up to ${max}. Type a question and press Enter. People pick these with one click.`}
      placeholder={value.length >= max ? 'That is the maximum' : 'Type a question and press Enter'}
      value={value}
      onChange={handle}
      maxTags={max}
      splitChars={['|']}
      acceptValueOnBlur
      clearable
      disabled={disabled}
      error={error}
      onSearchChange={() => error && setError(null)}
    />
  );
}
